/**
 * Module summary:
 * This helper loads the Google Maps JavaScript Places library on demand and
 * exposes thin wrappers for autocomplete suggestions and place details lookups.
 */

type GoogleMapsWindow = Window & {
  google?: any;
  __machaGoogleMapsLoader?: Promise<any>;
};

type GoogleAddressParts = {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  formattedAddress: string;
};

export type GoogleAddressSuggestion = {
  id: string;
  label: string;
  primaryText: string;
  secondaryText: string;
  toAddressParts: () => Promise<GoogleAddressParts | null>;
};

const GOOGLE_MAPS_SCRIPT_ID = 'macha-google-maps-script';

function getMapsKey(): string {
  return (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
}

async function ensureGoogleMapsLoaded(): Promise<any> {
  const mapsWindow = window as GoogleMapsWindow;
  const apiKey = getMapsKey();

  if (!apiKey) {
    throw new Error('Google Maps API key is missing.');
  }

  if (mapsWindow.google?.maps?.importLibrary) {
    return mapsWindow.google;
  }

  if (mapsWindow.__machaGoogleMapsLoader) {
    return mapsWindow.__machaGoogleMapsLoader;
  }

  mapsWindow.__machaGoogleMapsLoader = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(mapsWindow.google), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&v=weekly`;
    script.onload = () => resolve(mapsWindow.google);
    script.onerror = () => reject(new Error('Failed to load Google Maps.'));
    document.head.appendChild(script);
  });

  return mapsWindow.__machaGoogleMapsLoader;
}

async function getPlacesLibrary(): Promise<any> {
  const google = await ensureGoogleMapsLoaded();
  return google.maps.importLibrary('places');
}

function getAddressComponent(place: any, type: string): any | undefined {
  return (place?.addressComponents || []).find((component: any) => Array.isArray(component.types) && component.types.includes(type));
}

function getCity(place: any): string {
  return (
    getAddressComponent(place, 'locality')?.longText ||
    getAddressComponent(place, 'postal_town')?.longText ||
    getAddressComponent(place, 'sublocality')?.longText ||
    getAddressComponent(place, 'administrative_area_level_2')?.longText ||
    ''
  );
}

function toAddressParts(place: any): GoogleAddressParts | null {
  const streetNumber = getAddressComponent(place, 'street_number')?.longText || '';
  const route = getAddressComponent(place, 'route')?.longText || '';
  const postalCode = getAddressComponent(place, 'postal_code')?.longText || '';
  const postalCodeSuffix = getAddressComponent(place, 'postal_code_suffix')?.longText || '';
  const state = getAddressComponent(place, 'administrative_area_level_1')?.shortText || '';
  const city = getCity(place);
  const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim();
  const zipCode = postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode;

  if (!streetAddress && !city && !state && !zipCode) return null;

  return {
    streetAddress,
    city,
    state,
    zipCode,
    formattedAddress: place?.formattedAddress || [streetAddress, city, state, zipCode].filter(Boolean).join(', '),
  };
}

export async function createGoogleAutocompleteSessionToken(): Promise<any | null> {
  const places = await getPlacesLibrary();
  return places.AutocompleteSessionToken ? new places.AutocompleteSessionToken() : null;
}

export async function fetchGoogleAddressSuggestions(input: string, sessionToken?: any): Promise<GoogleAddressSuggestion[]> {
  const normalizedInput = input.trim();
  if (!normalizedInput) return [];

  const places = await getPlacesLibrary();
  const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input: normalizedInput,
    includedRegionCodes: ['us'],
    sessionToken,
  });

  return (suggestions || [])
    .map((suggestion: any, index: number) => {
      const prediction = suggestion.placePrediction;
      if (!prediction) return null;

      const text = prediction.text?.text || prediction.mainText?.text || '';
      const secondaryText = prediction.secondaryText?.text || '';

      return {
        id: prediction.placeId || prediction.text?.text || `google-suggestion-${index}`,
        label: [text, secondaryText].filter(Boolean).join(', '),
        primaryText: text,
        secondaryText,
        toAddressParts: async () => {
          const place = prediction.toPlace();
          await place.fetchFields({
            fields: ['formattedAddress', 'addressComponents'],
          });
          return toAddressParts(place);
        },
      } as GoogleAddressSuggestion;
    })
    .filter((entry: GoogleAddressSuggestion | null): entry is GoogleAddressSuggestion => Boolean(entry));
}

